// WebSocketサービス - グローバルなWebSocket接続管理
import { showNotification } from '@mantine/notifications';

export interface NFCCardData {
  uid: string;
  type: number; // 1=スタッフ, 2=顧客
  entity_id: number;
  timestamp: string;
}

export interface WebSocketMessage {
  event: string;
  data?: any;
  message?: string;
  context?: string;
}

type MessageHandler = (message: WebSocketMessage) => void;

class WebSocketService {
  private socket: WebSocket | null = null;
  private isConnected = false;
  private handlers: Map<string, Set<MessageHandler>> = new Map();
  private reconnectTimer: NodeJS.Timeout | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private deviceId: string;
  private recentlyWrittenCards: Set<string> = new Set();
  private isInWriteMode = false;
  private writeTimeoutTimer: NodeJS.Timeout | null = null;

  constructor() {
    // デバイスIDを生成または取得
    this.deviceId = this.getDeviceId();
  }

  private getDeviceId(): string {
    let deviceId = localStorage.getItem('device_id');
    if (!deviceId) {
      deviceId = `device_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
      localStorage.setItem('device_id', deviceId);
    }
    return deviceId;
  }

  async connect(): Promise<void> {
    if (this.socket?.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      // 固定ポート9876を使用
      const port = 9876; // 固定ポート番号

      this.socket = new WebSocket(`ws://localhost:${port}`);

      this.socket.onopen = () => {
        this.isConnected = true;
        this.reconnectAttempts = 0;

        // 接続時に書き込みモードをリセット
        this.isInWriteMode = false;
        this.recentlyWrittenCards.clear();
        if (this.writeTimeoutTimer) {
          clearTimeout(this.writeTimeoutTimer);
          this.writeTimeoutTimer = null;
        }

        // デバイスIDを登録
        this.send({
          event: 'device_register',
          device_id: this.deviceId,
        });

        // 接続成功をイベントとして通知
        this.notifyHandlers({ event: 'connected' });
      };

      this.socket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          this.notifyHandlers(message);
        } catch (_error) {
          // Failed to parse WebSocket message
        }
      };

      this.socket.onerror = (_error) => {
        this.isConnected = false;
      };

      this.socket.onclose = () => {
        this.isConnected = false;
        this.notifyHandlers({ event: 'disconnected' });
        this.attemptReconnect();
      };
    } catch (_error) {
      this.attemptReconnect();
    }
  }

  private attemptReconnect(): void {
    if (this.reconnectTimer) return;

    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);

      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null;
        this.connect();
      }, delay);
    } else {
      showNotification({
        title: 'NFC接続エラー',
        message:
          'NFCサービスに接続できません。ローカルのNFCサービスが起動していることを確認してください。',
        color: 'red',
        autoClose: false,
      });
    }
  }

  disconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }

    this.isConnected = false;
  }

  send(data: any): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(data));
    }
  }

  // イベントハンドラの登録
  on(event: string, handler: MessageHandler): () => void {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set());
    }
    this.handlers.get(event)!.add(handler);

    // アンサブスクライブ関数を返す
    return () => {
      this.handlers.get(event)?.delete(handler);
    };
  }

  // 特定のイベントハンドラを全て削除
  off(event: string): void {
    this.handlers.delete(event);
  }

  // 全てのハンドラを削除
  clear(): void {
    this.handlers.clear();
  }

  private notifyHandlers(message: WebSocketMessage): void {
    // card_writtenイベントの場合、書き込みモードを終了してUIDを記録
    if (message.event === 'card_written') {
      this.isInWriteMode = false;
      if (this.writeTimeoutTimer) {
        clearTimeout(this.writeTimeoutTimer);
        this.writeTimeoutTimer = null;
      }

      if (message.data?.uid) {
        const uid = message.data.uid;
        this.recentlyWrittenCards.add(uid);

        // 10秒後に削除（書き込み→登録の処理時間を考慮）
        setTimeout(() => {
          this.recentlyWrittenCards.delete(uid);
        }, 10000);
      }
    }

    // エラーイベントで書き込みモード中の場合、書き込みモードを終了
    if (message.event === 'error' && message.context === 'write') {
      this.isInWriteMode = false;
      if (this.writeTimeoutTimer) {
        clearTimeout(this.writeTimeoutTimer);
        this.writeTimeoutTimer = null;
      }
    }

    // nfc_detectedイベントで、書き込みモード中または最近書き込まれたカードの場合はスキップ
    if (message.event === 'nfc_detected') {
      if (this.isInWriteMode) {
        return; // ハンドラーを呼び出さない
      }

      if (message.data?.uid && this.recentlyWrittenCards.has(message.data.uid)) {
        return; // ハンドラーを呼び出さない
      }
    }

    // 特定のイベントハンドラを実行
    const eventHandlers = this.handlers.get(message.event);
    if (eventHandlers) {
      eventHandlers.forEach((handler) => {
        try {
          handler(message);
        } catch (_error) {
          // Handler error
        }
      });
    }

    // ワイルドカードハンドラを実行
    const wildcardHandlers = this.handlers.get('*');
    if (wildcardHandlers) {
      wildcardHandlers.forEach((handler) => handler(message));
    }
  }

  getConnectionState(): boolean {
    return this.isConnected;
  }

  getWriteMode(): boolean {
    return this.isInWriteMode;
  }

  // NFCカード書き込みリクエスト
  requestWriteCard(type: number, entityId: number): void {
    // 書き込みモードを開始
    this.isInWriteMode = true;

    // 15秒のタイムアウトを設定（書き込みが失敗した場合のフェイルセーフ）
    if (this.writeTimeoutTimer) {
      clearTimeout(this.writeTimeoutTimer);
    }
    this.writeTimeoutTimer = setTimeout(() => {
      if (this.isInWriteMode) {
        this.isInWriteMode = false;
        this.writeTimeoutTimer = null;
      }
    }, 15000);

    const writeData = {
      event: 'write_card',
      data: {
        type,
        entity_id: entityId,
      },
    };

    this.send(writeData);
  }
}

// シングルトンインスタンス
export const websocketService = new WebSocketService();
